import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import AttemptPatternPieChartCard from '../Components/AttemptPatternPieChartCard';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  usePagination,
  PaginationEllipsis,
} from '../../components/ui/pagination';
import QuestionDrawer from '../../components/drawers/QuestionDrawer';
import { formatTestTitle } from '../../utils/formatTestTitle';
import { ContentAreaSkeleton, FiltersSkeleton } from '../Components/MistakesLoaders';
import QuestionListItem from '../Components/QuestionListItem';
import { AppLayout } from '../../Layout/AppLayout';
import { usePackStorage } from '../../Layout/usePackStorage';

const transformDetailedDataForPieChart = (tags, totalIncorrect, totalUnattempted) => {
  if (!tags || tags.length === 0) return [];
  return tags.map((tag) => {
    const incorrectCount = tag.incorrectCount || 0;
    const unattemptedCount = tag.unattemptedCount || 0;
    const incorrectPercentage = totalIncorrect > 0 ? (incorrectCount / totalIncorrect) * 100 : 0;
    const unattemptedPercentage = totalUnattempted > 0 ? (unattemptedCount / totalUnattempted) * 100 : 0;
    return {
      tagTitle: tag.title,
      icon: tag.icon,
      incorrectPercentage: incorrectPercentage.toFixed(2),
      unattemptedPercentage: unattemptedPercentage.toFixed(2),
    };
  });
};

export default function ChapterWiseMistakesDetailed() {
  const { purchasedPacks, selectedPack, handlePackChange } = usePackStorage();

  const { chapterId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { chapterTitle, specificTestOptions } = location.state || { chapterTitle: 'Chapter', specificTestOptions: [] };

  const [attemptingPatternExpanded, setAttemptingPatternExpanded] = useState(false);
  const [detailedChapterQuestions, setDetailedChapterQuestions] = useState([]);
  const [detailedChapterTotalPages, setDetailedChapterTotalPages] = useState(1);
  const [detailedChapterCurrentPage, setDetailedChapterCurrentPage] = useState(1);
  const [detailedChapterQuestionFilter, setDetailedChapterQuestionFilter] = useState('All');
  const [communitySolutionFilter, setCommunitySolutionFilter] = useState(false);
  const [selectedDetailedChapterTest, setSelectedDetailedChapterTest] = useState('All');
  const [selectedDetailedChapterMistakeReason, setSelectedDetailedChapterMistakeReason] = useState('All');
  const [detailedMistakeReasonsOptions, setDetailedMistakeReasonsOptions] = useState([]);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [pieChartData, setPieChartData] = useState([]);

  const [isDetailedTestSelectOpen, setIsDetailedTestSelectOpen] = useState(false);
  const [isDetailedMistakeReasonSelectOpen, setIsDetailedMistakeReasonSelectOpen] = useState(false);
  const [isQuestionDrawerOpen, setIsQuestionDrawerOpen] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const paginationRange = usePagination({
    totalPages: detailedChapterTotalPages,
    currentPage: detailedChapterCurrentPage,
  });
  const selectedPackId = localStorage.getItem('selectedPackId');

  useEffect(() => {
    if (!chapterId) return;
    setIsLoadingContent(true);
    const params = {
      offset: (detailedChapterCurrentPage - 1) * 10,
      limit: 10,
      packId: selectedPackId,
      ...(detailedChapterQuestionFilter === 'Attempted Incorrect' && { incorrect: 'true' }),
      ...(detailedChapterQuestionFilter === 'Not Attempted' && { unattempted: 'true' }),
      ...(detailedChapterQuestionFilter === 'Important' && { important: 'true' }),
      ...(communitySolutionFilter && { communitySolution: 'true' }),
      ...(selectedDetailedChapterTest !== 'All' && { test: selectedDetailedChapterTest }),
      ...(selectedDetailedChapterMistakeReason !== 'All' && { mistakeReason: selectedDetailedChapterMistakeReason }),
    };
    axios
      .get(`/api/v2/answers/${chapterId}/chapter-wise-detailed`, { params })
      .then((response) => {
        if (response.data.success) {
          setDetailedChapterQuestions(Array.isArray(response.data.data) ? response.data.data : []);
          setDetailedChapterTotalPages(response.data.totalPages || 1);
          setDetailedChapterCurrentPage(response.data.currentPage || 1);
        } else {
          setDetailedChapterQuestions([]);
          setDetailedChapterTotalPages(1);
        }
      })
      .catch((error) => {
        console.error(`Error fetching detailed questions for chapter ${chapterId}:`, error);
      })
      .finally(() => {
        setIsLoadingContent(false);
      });
  }, [
    chapterId,
    detailedChapterCurrentPage,
    detailedChapterQuestionFilter,
    communitySolutionFilter,
    selectedDetailedChapterTest,
    selectedDetailedChapterMistakeReason,
  ]);

  useEffect(() => {
    if (!chapterId) return;
    setIsLoadingFilters(true);
    axios
      .get(`/api/v2/answers/${chapterId}/chapter-wise-detailed`, { params: { limit: 1, packId: selectedPackId } })
      .then((response) => {
        if (response.data.success) {
          const { userQuestionTags, totalIncorrect, totalUnattempted } = response.data;
          setPieChartData(transformDetailedDataForPieChart(userQuestionTags, totalIncorrect, totalUnattempted));
          if (userQuestionTags) {
            const reasons = userQuestionTags
              .filter((tag) => tag.title !== 'Not Tagged')
              .map((item) => ({ id: item._id, title: item.title }));
            setDetailedMistakeReasonsOptions([{ id: 'All', title: 'All' }, ...reasons]);
          }
        }
      })
      .catch((error) => console.error(`Error fetching initial data for chapter ${chapterId}:`, error))
      .finally(() => setIsLoadingFilters(false));
  }, [chapterId]);

  const handleQuestionClick = (questionIndex) => {
    setCurrentQuestionIndex(questionIndex);
    setIsQuestionDrawerOpen(true);
  };

  const handleDetailedPageChange = (page) => {
    setDetailedChapterCurrentPage(page);
  };

  const handleQuestionChange = (newIndex) => {
    setCurrentQuestionIndex(newIndex);
  };

  const currentQuestion = detailedChapterQuestions[currentQuestionIndex];

  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
      <div className="flex-1 min-w-0 overflow-hidden flex justify-center">
        <div className="h-full w-full max-w-7xl">
          <div className="w-full px-4 py-6 md:py-8">
            <div className="flex flex-col items-start gap-2 mb-6">
              <Button
                onClick={() => navigate(`/v3/my-mistakes?type=chapter-wise`)}
                size="icon"
                className="text-[var(--q3-neutral-light)] mb-2 bg-[var(--q3-surface-dimmer)] hover:bg-[var(--q3-surface-dimmer)]/90 rounded-full"
              >
                <ArrowLeft size={24} />
              </Button>
              <div>
                <h1 className="text-2xl font-semibold text-[var(--q3-neutral-default)]">{chapterTitle}</h1>
              </div>
            </div>

            {isLoadingFilters ? (
              <FiltersSkeleton />
            ) : (
              <div className="flex items-center flex-wrap gap-2 mb-6">
                <Select
                  value={selectedDetailedChapterTest}
                  onValueChange={setSelectedDetailedChapterTest}
                  onOpenChange={setIsDetailedTestSelectOpen}
                >
                  <SelectTrigger className={`w-auto cursor-pointer rounded-full`}>
                    <SelectValue placeholder="Select Test" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Tests</SelectItem>
                    {specificTestOptions &&
                      specificTestOptions.map((test) => (
                        <SelectItem key={test._id} value={test._id}>
                          {test.title}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <div className="h-8 w-[1px] bg-[var(--q3-stroke-normal)]" />
                <Select
                  value={selectedDetailedChapterMistakeReason}
                  onValueChange={setSelectedDetailedChapterMistakeReason}
                  onOpenChange={setIsDetailedMistakeReasonSelectOpen}
                >
                  <SelectTrigger className={`w-auto cursor-pointer rounded-full`}>
                    Mistake Reason : <SelectValue placeholder="Mistake Reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {detailedMistakeReasonsOptions.map((reason) => (
                      <SelectItem key={reason.id} value={reason.id}>
                        {reason.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="h-8 w-[1px] bg-[var(--q3-stroke-normal)]" />
                <Button
                  variant={detailedChapterQuestionFilter === 'All' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDetailedChapterQuestionFilter('All')}
                  className={`${detailedChapterQuestionFilter === 'All' ? 'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]' : ''} rounded-full`}
                >
                  All Qs {detailedChapterQuestionFilter === 'All' && <Check className="ml-2 h-4 w-4" />}
                </Button>
                <Button
                  variant={detailedChapterQuestionFilter === 'Attempted Incorrect' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDetailedChapterQuestionFilter('Attempted Incorrect')}
                  className={`${detailedChapterQuestionFilter === 'Attempted Incorrect' ? 'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]' : ''} rounded-full`}
                >
                  Attempted Incorrect{' '}
                  {detailedChapterQuestionFilter === 'Attempted Incorrect' && <Check className="ml-2 h-4 w-4" />}
                </Button>
                <Button
                  variant={detailedChapterQuestionFilter === 'Not Attempted' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDetailedChapterQuestionFilter('Not Attempted')}
                  className={`${detailedChapterQuestionFilter === 'Not Attempted' ? 'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]' : ''} rounded-full`}
                >
                  Not Attempted{' '}
                  {detailedChapterQuestionFilter === 'Not Attempted' && <Check className="ml-2 h-4 w-4" />}
                </Button>
                <Button
                  variant={detailedChapterQuestionFilter === 'Important' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDetailedChapterQuestionFilter('Important')}
                  className={`${detailedChapterQuestionFilter === 'Important' ? 'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]' : ''} rounded-full`}
                >
                  Important {detailedChapterQuestionFilter === 'Important' && <Check className="ml-2 h-4 w-4" />}
                </Button>
                <div className="h-8 w-[1px] bg-[var(--q3-stroke-normal)]" />
                <Button
                  variant={communitySolutionFilter ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCommunitySolutionFilter((prev) => !prev)}
                  className={`${communitySolutionFilter ? 'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]' : ''} rounded-full`}
                >
                  My Added Solutions {communitySolutionFilter && <Check className="ml-2 h-4 w-4" />}
                </Button>
              </div>
            )}

            {isLoadingContent ? (
              <ContentAreaSkeleton />
            ) : (
              <>
                {selectedDetailedChapterMistakeReason === 'All' && (
                  <AttemptPatternPieChartCard
                    data={pieChartData}
                    expanded={attemptingPatternExpanded}
                    onToggleExpand={() => setAttemptingPatternExpanded(!attemptingPatternExpanded)}
                  />
                )}

                <Card className="gap-0 pt-2 mb-20 md:mb-0">
                  <CardHeader>
                    <CardTitle className="text-lg text-[var(--q3-neutral-default)] py-3">All Mistaken Qs</CardTitle>
                  </CardHeader>
                  <hr />
                  <CardContent className="py-0 px-0">
                    <div className="space-y-0">
                      {detailedChapterQuestions.length > 0 ? (
                        detailedChapterQuestions.map((question, index) => (
                          <QuestionListItem
                            key={question._id}
                            question={question}
                            index={index}
                            currentPage={detailedChapterCurrentPage}
                            onClick={() => handleQuestionClick(index)}
                            displayTitle={formatTestTitle(question?.test[0]?.title)}
                            packId={selectedPackId}
                            testId={question?.test[0]?._id}
                          />
                        ))
                      ) : (
                        <p className="text-center text-[var(--q3-neutral-light)] py-4">
                          {communitySolutionFilter
                            ? 'Community Solution not added for any of the questions'
                            : 'No questions found for the selected filters.'}
                        </p>
                      )}
                    </div>
                    {detailedChapterTotalPages > 1 && (
                      <Pagination className="mt-6">
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious
                              onClick={(e) => {
                                e.preventDefault();
                                handleDetailedPageChange(detailedChapterCurrentPage - 1);
                              }}
                              className={detailedChapterCurrentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                            />
                          </PaginationItem>
                          {paginationRange &&
                            paginationRange.map((pageNumber, index) => {
                              if (typeof pageNumber === 'string') {
                                return (
                                  <PaginationItem key={`ellipsis-${index}`}>
                                    <PaginationEllipsis />
                                  </PaginationItem>
                                );
                              }
                              return (
                                <PaginationItem key={pageNumber}>
                                  <PaginationLink
                                    href="#"
                                    isActive={pageNumber === detailedChapterCurrentPage}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleDetailedPageChange(pageNumber);
                                    }}
                                  >
                                    {pageNumber}
                                  </PaginationLink>
                                </PaginationItem>
                              );
                            })}
                          <PaginationItem>
                            <PaginationNext
                              onClick={(e) => {
                                e.preventDefault();
                                handleDetailedPageChange(detailedChapterCurrentPage + 1);
                              }}
                              className={
                                detailedChapterCurrentPage === detailedChapterTotalPages
                                  ? 'pointer-events-none opacity-50'
                                  : ''
                              }
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
            <QuestionDrawer
              isOpen={isQuestionDrawerOpen}
              onClose={() => setIsQuestionDrawerOpen(false)}
              questions={detailedChapterQuestions}
              currentQuestionIndex={currentQuestionIndex}
              onQuestionChange={handleQuestionChange}
              packId={selectedPackId}
              testId={currentQuestion?.test[0]?._id}
              isChapterWise={true}
            />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
